import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { 
  Shield, 
  AlertCircle, 
  Loader, 
  ArrowLeft, 
  Eye, 
  EyeOff, 
  Lock, 
  User, 
  Crown,
  Database,
  Users,
  Settings
} from 'lucide-react';

const AdminLogin: React.FC = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { login, loading } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!username.trim() || !password.trim()) {
      setError('Usuario y contraseña son requeridos');
      return;
    }

    try {
      const success = await login(username, password);
      if (!success) {
        setError('Credenciales inválidas');
      }
      // La redirección se maneja automáticamente en AuthContext
    } catch (err: any) {
      setError(err.message || 'Error al iniciar sesión');
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #1e293b 0%, #1e40af 50%, #4c1d95 100%)',
      padding: '1rem',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Elementos decorativos de fondo */}
      <div style={{
        position: 'absolute',
        inset: 0,
        overflow: 'hidden'
      }}>
        {/* Círculos animados de fondo */}
        <motion.div
          animate={{ 
            rotate: 360,
            scale: [1, 1.3, 1],
            opacity: [0.1, 0.2, 0.1]
          }}
          transition={{ 
            duration: 25, 
            repeat: Infinity, 
            ease: "linear" 
          }}
          style={{
            position: 'absolute',
            top: '-10rem',
            right: '-10rem',
            width: '24rem',
            height: '24rem',
            background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(147, 51, 234, 0.2) 100%)',
            borderRadius: '50%',
            filter: 'blur(3rem)'
          }}
        />
        <motion.div
          animate={{ 
            rotate: -360,
            scale: [1, 0.7, 1],
            opacity: [0.1, 0.3, 0.1]
          }}
          transition={{ 
            duration: 35, 
            repeat: Infinity, 
            ease: "linear" 
          }}
          style={{
            position: 'absolute',
            bottom: '-10rem',
            left: '-10rem',
            width: '20rem',
            height: '20rem',
            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(236, 72, 153, 0.2) 100%)',
            borderRadius: '50%',
            filter: 'blur(3rem)'
          }}
        />
        <motion.div
          animate={{ 
            rotate: 180,
            scale: [1, 1.1, 1],
            opacity: [0.05, 0.15, 0.05]
          }}
          transition={{ 
            duration: 20, 
            repeat: Infinity, 
            ease: "easeInOut" 
          }}
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '16rem',
            height: '16rem',
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(59, 130, 246, 0.1) 100%)',
            borderRadius: '50%',
            filter: 'blur(2rem)'
          }}
        />
        
        {/* Patrones geométricos */}
        <div style={{
          position: 'absolute',
          inset: 0,
          opacity: 0.05
        }}>
          <div style={{
            position: 'absolute',
            top: '5rem',
            left: '5rem',
            width: '8rem',
            height: '8rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transform: 'rotate(45deg)'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '5rem',
            right: '5rem',
            width: '6rem',
            height: '6rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transform: 'rotate(12deg)'
          }}></div>
          <div style={{
            position: 'absolute',
            top: '50%',
            right: '25%',
            width: '4rem',
            height: '4rem',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transform: 'rotate(45deg)'
          }}></div>
        </div>
      </div>

      {/* Contenedor principal centrado */}
      <div style={{
        width: '100%',
        maxWidth: '72rem',
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: '1.5rem',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            padding: '3rem',
            maxWidth: '28rem',
            width: '100%',
            position: 'relative',
            zIndex: 10,
            border: '1px solid rgba(255, 255, 255, 0.2)',
            transition: 'box-shadow 0.3s ease'
          }}
        >
        {/* Header profesional */}
        <div style={{
          textAlign: 'center',
          marginBottom: '3rem'
        }}>
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
            style={{
              position: 'relative',
              marginBottom: '2rem'
            }}
          >
            {/* Logo principal */}
            <div style={{
              width: '6rem',
              height: '6rem',
              margin: '0 auto',
              background: 'linear-gradient(135deg, #2563eb 0%, #4f46e5 50%, #7c3aed 100%)',
              borderRadius: '1rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <Crown style={{ color: 'white', zIndex: 10 }} size={40} />
              {/* Efecto de brillo */}
              <div style={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 100%)',
                borderRadius: '1rem'
              }}></div>
            </div>
          </motion.div>
          
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            style={{
              fontSize: '2.5rem',
              fontWeight: 'bold',
              background: 'linear-gradient(90deg, #1f2937 0%, #2563eb 50%, #4f46e5 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              marginBottom: '0.5rem',
              letterSpacing: '-0.025em'
            }}
          >
            Admin Panel
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            style={{
              color: '#6b7280',
              fontWeight: '600',
              fontSize: '1.125rem',
              marginBottom: '2rem',
              letterSpacing: '0.025em'
            }}
          >
            CHILDREN IBC
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            style={{
              background: 'linear-gradient(90deg, #eff6ff 0%, #e0e7ff 100%)',
              border: '1px solid #bfdbfe',
              borderRadius: '0.75rem',
              padding: '1rem',
              marginBottom: '1.5rem'
            }}
          >
            <p style={{
              fontSize: '0.875rem',
              color: '#6b7280',
              fontWeight: '500',
              margin: 0
            }}>
              Acceso seguro al panel de administración
            </p>
          </motion.div>
        </div>

        {/* Formulario profesional */}
        <motion.form
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8 }}
          onSubmit={handleSubmit} 
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1.5rem'
          }}
        >
          <div>
            <label htmlFor="username" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Usuario
            </label>
            <div style={{
              position: 'relative'
            }}>
              <input
                type="text"
                id="username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Ingrese su usuario"
                style={{
                  width: '100%',
                  paddingLeft: '3rem',
                  paddingRight: '1rem',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  outline: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={loading}
              />
              <User style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                pointerEvents: 'none'
              }} size={18} />
            </div>
          </div>

          <div>
            <label htmlFor="password" style={{
              display: 'block',
              fontSize: '0.875rem',
              fontWeight: '600',
              color: '#374151',
              marginBottom: '0.5rem'
            }}>
              Contraseña
            </label>
            <div style={{
              position: 'relative'
            }}>
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Ingrese su contraseña"
                style={{
                  width: '100%',
                  paddingLeft: '3rem',
                  paddingRight: '3rem',
                  paddingTop: '0.75rem',
                  paddingBottom: '0.75rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '0.5rem',
                  outline: 'none',
                  fontSize: '1rem',
                  transition: 'all 0.3s ease',
                  backgroundColor: 'white'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#3b82f6';
                  e.target.style.boxShadow = '0 0 0 3px rgba(59, 130, 246, 0.1)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#e5e7eb';
                  e.target.style.boxShadow = 'none';
                }}
                disabled={loading}
              />
              <Lock style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af',
                pointerEvents: 'none'
              }} size={18} />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  position: 'absolute',
                  right: '1rem',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '0.25rem',
                  transition: 'color 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.color = '#6b7280';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.color = '#9ca3af';
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                padding: '1rem',
                borderRadius: '0.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
              }}
            >
              <AlertCircle style={{
                color: '#ef4444',
                marginRight: '0.75rem',
                flexShrink: 0,
                marginTop: '0.125rem'
              }} size={18} />
              <p style={{
                color: '#b91c1c',
                fontSize: '0.875rem',
                fontWeight: '500',
                margin: 0
              }}>{error}</p>
            </motion.div>
          )}

          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            disabled={loading || !username.trim() || !password.trim()}
            style={{
              width: '100%',
              background: 'linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)',
              color: 'white',
              padding: '0.75rem',
              borderRadius: '0.5rem',
              fontWeight: '600',
              fontSize: '1rem',
              border: 'none',
              cursor: loading || !username.trim() || !password.trim() ? 'not-allowed' : 'pointer',
              opacity: loading || !username.trim() || !password.trim() ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
              transition: 'all 0.3s ease',
              transform: loading || !username.trim() || !password.trim() ? 'none' : 'scale(1)'
            }}
            onMouseEnter={(e) => {
              if (!loading && username.trim() && password.trim()) {
                e.currentTarget.style.background = 'linear-gradient(90deg, #1d4ed8 0%, #3730a3 100%)';
                e.currentTarget.style.boxShadow = '0 20px 25px -5px rgba(0, 0, 0, 0.1)';
              }
            }}
            onMouseLeave={(e) => {
              if (!loading && username.trim() && password.trim()) {
                e.currentTarget.style.background = 'linear-gradient(90deg, #2563eb 0%, #4f46e5 100%)';
                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
              }
            }}
          >
            {loading ? (
              <>
                <Loader style={{
                  animation: 'spin 1s linear infinite',
                  marginRight: '0.5rem'
                }} size={20} />
                Autenticando...
              </>
            ) : (
              <>
                <Shield style={{ marginRight: '0.5rem' }} size={20} />
                Acceder al Dashboard
              </>
            )}
          </motion.button>
        </motion.form>

        {/* Footer elegante */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.0 }}
          style={{
            marginTop: '2rem',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '1rem'
          }}
        >
          <div style={{
            borderTop: '1px solid #e5e7eb',
            paddingTop: '1.5rem'
          }}>
            <button
              onClick={() => navigate('/')}
              style={{
                color: '#6b7280',
                fontSize: '0.875rem',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = '#2563eb';
                e.currentTarget.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = '#6b7280';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <ArrowLeft style={{ marginRight: '0.5rem' }} size={16} />
              Volver al Inicio
            </button>
          </div>
          
          <div style={{
            fontSize: '0.75rem',
            color: '#9ca3af'
          }}>
            © 2024 CHILDREN IBC. Todos los derechos reservados.
          </div>
        </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminLogin;
